/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import logo from '../../Assets/Images/logo.png';
import { auth, functions } from '../../firebase';
import LoaderComponent from '../../Shared/Loader';
import { getConfirmation } from '../../utils/ui';
import { rejectionMessage } from '../../utils/rejectionMessage';
import { openJobDateLabel } from './openJobDate';
import { OPEN_JOB_ADDRESS_NEEDS_FIXING, openJobShopAddressNeedsFixing } from './openJobShopAddress';
import { inviteTokenFromSearch } from '../../utils/inviteTokenFromSearch';
import {
    OPEN_JOB_ACCEPT_CALLABLE,
    OPEN_JOB_CHECK_CALLABLE,
    openJobAssignmentPayload,
    openJobLoginLocation,
} from './openJobRoute';

export { openJobAssignmentPayload } from './openJobRoute';

export const OPEN_JOB_LOAD_REJECTION_FALLBACK = 'Could not open this job. Please try again.';
export const OPEN_JOB_ACCEPT_REJECTION_FALLBACK = 'Could not accept this job. Please try again.';

export const OPEN_JOB_INVITE_OTHER_TECH = 'This invite belongs to another tech.';

export const OPEN_JOB_SIGN_IN_REQUIRED = 'Sign in as the invited tech to open this job.';

export const OPEN_JOB_TOKEN_REFRESH_WARNING = 'Could not refresh the sign-in token.';

const OpenJobPage = ({ match, history, location }: { match: any; history: any; location?: { search?: string } }) => {
    const [jobDetail, setJobDetail] = useState<any>({});
    const [invitePayload, setInvitePayload] = useState<unknown>(null);
    const [error, setError] = useState('');
    const [refreshWarning, setRefreshWarning] = useState('');
    const [isJobOpen, setIsJobOpen] = useState(false);
    const [showLoader, setShowLoader] = useState(false);
    /** Route ids whose availability check succeeded. Accept sends the current route, so the card stays hidden until these match. */
    const [confirmedInvite, setConfirmedInvite] = useState<{
        jobId: string | null;
        techId: string | null;
        regionId: string | null;
    } | null>(null);
    /** techId returned by the availability check. Accept refuses a signed-in uid that is not this id. */
    const [checkedTechId, setCheckedTechId] = useState<string | null>(null);
    /** Synchronous lock so a second click cannot call acceptAssignmentInvite before the first await. */
    const acceptLock = useRef(false);
    const [acceptBusy, setAcceptBusy] = useState(false);
    /** Incremented for each availability check. A response is applied only when its stamp is still current. */
    const inviteCheckStamp = useRef(0);

    const techId = useMemo(() => (match.params && match.params.techId ? match.params.techId : null), [match]);
    const regionId = useMemo(() => (match.params ? match.params.regionId : null), [match]);
    const jobId = useMemo(() => (match.params ? match.params.jobId : null), [match]);
    const inviteToken = useMemo(() => inviteTokenFromSearch(location?.search), [location]);
    const signedInUid = auth.currentUser && auth.currentUser.uid ? auth.currentUser.uid : null;
    const loginTo = openJobLoginLocation(typeof match.url === 'string' ? match.url : '', location?.search || '');

    const showOpenInvite =
        isJobOpen === true &&
        confirmedInvite !== null &&
        confirmedInvite.jobId === jobId &&
        confirmedInvite.techId === techId &&
        confirmedInvite.regionId === regionId;

    const checkIfAssignmentAvailable = useCallback(async () => {
        const inviteIds = openJobAssignmentPayload(regionId, jobId, techId, inviteToken);
        if (!inviteIds) return;
        const stamp = ++inviteCheckStamp.current;
        setShowLoader(true);
        setError('');
        setConfirmedInvite(null);
        setCheckedTechId(null);
        setInvitePayload(null);
        setJobDetail({});
        setIsJobOpen(false);
        const stillCurrent = () => stamp === inviteCheckStamp.current;
        try {
            const { data } = await functions.httpsCallable(OPEN_JOB_CHECK_CALLABLE)(inviteIds);
            if (!stillCurrent()) return;
            const payload = data?.data;
            const returnedTechId =
                payload && typeof payload.techId === 'string' && payload.techId ? payload.techId : null;
            setInvitePayload(payload ?? null);
            setCheckedTechId(returnedTechId);
            setJobDetail(payload.job);
            setIsJobOpen(payload.open);
            setConfirmedInvite({ jobId, techId, regionId });
        } catch (errors) {
            if (!stillCurrent()) return;
            setError(rejectionMessage(errors, OPEN_JOB_LOAD_REJECTION_FALLBACK));
        } finally {
            if (stillCurrent()) {
                setShowLoader(false);
            }
        }
    }, [jobId, techId, regionId, inviteToken]);

    const assignCurrentTech = async () => {
        if (acceptLock.current) return;
        if (!auth.currentUser || !auth.currentUser.uid) {
            setError(OPEN_JOB_SIGN_IN_REQUIRED);
            return;
        }
        if (techId && auth.currentUser.uid !== techId) {
            setError(OPEN_JOB_INVITE_OTHER_TECH);
            return;
        }
        if (checkedTechId && auth.currentUser.uid !== checkedTechId) {
            setError(OPEN_JOB_INVITE_OTHER_TECH);
            return;
        }
        try {
            if (openJobShopAddressNeedsFixing(invitePayload, jobDetail)) {
                setError(OPEN_JOB_ADDRESS_NEEDS_FIXING);
                return;
            }
        } catch {
            setError(OPEN_JOB_ADDRESS_NEEDS_FIXING);
            return;
        }
        const inviteIds = openJobAssignmentPayload(regionId, jobId, techId, inviteToken);
        if (!inviteIds) return;
        acceptLock.current = true;
        setAcceptBusy(true);
        setShowLoader(true);
        let accepted = false;
        try {
            await functions.httpsCallable(OPEN_JOB_ACCEPT_CALLABLE)(inviteIds);
            accepted = true;
            try {
                await auth.currentUser?.getIdToken(true);
            } catch (refreshError) {
                const detail = rejectionMessage(refreshError, '');
                setRefreshWarning(
                    detail ? `${OPEN_JOB_TOKEN_REFRESH_WARNING} ${detail}` : OPEN_JOB_TOKEN_REFRESH_WARNING,
                );
            }
            await getConfirmation(
                'Success! This job is now yours.',
                ' Log in to view your active job and continue.',
                {
                    showCancelButton: false,
                    confirmButtonText: 'Go to login',
                    cancelButtonText: '',
                },
            );
            history.push('/login');
        } catch (errors) {
            setError(rejectionMessage(errors, OPEN_JOB_ACCEPT_REJECTION_FALLBACK));
        } finally {
            if (!accepted) {
                acceptLock.current = false;
                setAcceptBusy(false);
            }
        }
        setShowLoader(false);
    };

    useEffect(() => {
        if (!openJobAssignmentPayload(regionId, jobId, techId, inviteToken)) return;
        if (inviteToken && !signedInUid) {
            inviteCheckStamp.current += 1;
            setError(OPEN_JOB_SIGN_IN_REQUIRED);
            setShowLoader(false);
            setConfirmedInvite(null);
            setCheckedTechId(null);
            setInvitePayload(null);
            setJobDetail({});
            setIsJobOpen(false);
            return;
        }
        checkIfAssignmentAvailable();
    }, [regionId, techId, jobId, inviteToken, signedInUid, checkIfAssignmentAvailable]);

    return (
        <div className="bg-cover min-h-screen bg-gradient-to-b from-black to-blue-500">
            <LoaderComponent showLoader={showLoader} />
            <div className="flex flex-col">
                <div className="mt-20 text-center mx-auto">
                    <img src={logo} alt="Logo" className="text-center mx-auto w-8/12" />
                    <p className="text-lg font-medium text-center text-white mt-4">Technician Scheduling Made Easy</p>
                </div>
                <div className="text-center mx-auto mt-8 bg-white w-full md:w-3/5 rounded shadow py-4 px-2">
                    {showOpenInvite ? (
                        <>
                            {error ? (
                                <div className="py-4 bg-red-500 w-full text-white text-center mb-3">
                                    <span>{error}</span>
                                </div>
                            ) : null}
                            {refreshWarning ? (
                                <div className="py-4 bg-yellow-500 w-full text-gray-900 text-center mb-3" role="status">
                                    <span>{refreshWarning}</span>
                                </div>
                            ) : null}
                            <div className="uppercase text-xl font-medium px-3">
                                {jobDetail.status} {jobDetail.type} Job
                            </div>
                            <div className="text-3xl">
                                {jobDetail.vehicle?.year} {jobDetail.vehicle?.make} {jobDetail.vehicle?.model}
                            </div>
                            <div className="mt-4 text-base text-gray-600 text-center">
                                <p className="my-1">Insurance: {jobDetail.vehicle?.insurance}</p>
                                <p className="my-1">Contact: {jobDetail.contact}</p>
                                <p className="my-1">
                                    Date: {openJobDateLabel(jobDetail.date, jobDetail.timezone, jobDetail.timeZone)}
                                </p>
                                <p className="my-1">Drop-off: {jobDetail.dropped}</p>
                                <p className="my-1">
                                    {jobDetail.waiting ? 'Available to pick up' : 'Not available to pick up'}
                                </p>
                                <p className="my-1">Notes: {jobDetail.notes}</p>
                            </div>
                            <div className="my-8">
                                <button
                                    type="button"
                                    onClick={() => assignCurrentTech()}
                                    disabled={acceptLock.current || acceptBusy}
                                    className="text-lg bg-blue-500 text-white px-3 py-2 rounded w-72 capitalize"
                                >
                                    Assign yourself
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="py-4 bg-red-500 w-full text-white text-center mb-3">
                                {error ? <span>{error}</span> : 'Oh no, job is no longer available!'}
                            </div>
                            <div className="my-8">
                                <NavLink to={loginTo} className="text-lg bg-green-500 text-white px-3 py-2 rounded">
                                    Login
                                </NavLink>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
export default OpenJobPage;

OpenJobPage.propTypes = {
    match: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
};
